import React, { useState, useMemo } from "react";
import {
  Box,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";

const ShareStep = ({
  formData,
  setFormData,
  reduxUsers,
  usersLoading,
  formErrors,
  toggleUserSelection,
  toggleSelectAll,
  currentUser,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();

    const filtered = reduxUsers.filter(
      (user) =>
        user.fullName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.role?.toLowerCase().includes(term)
    );

    // Move current user to top
    if (currentUser) {
      const index = filtered.findIndex((u) => u.id === currentUser.id);
      if (index > -1) {
        const [user] = filtered.splice(index, 1);
        filtered.unshift(user);
      }
    }

    return filtered;
  }, [searchTerm, reduxUsers, currentUser]);

  const allUsersSelected =
    reduxUsers.length > 0 &&
    formData.selectedUsers.length === reduxUsers.length;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>
          Playlist Visibility
        </FormLabel>
        <RadioGroup
          row
          name="isPublic"
          value={formData.isPublic ? "public" : "private"}
          onChange={(e) => {
            const newVal = e.target.value;
            setFormData((prev) => ({
              ...prev,
              isPublic: newVal === "public",
              selectedUsers: newVal === "public" ? prev.selectedUsers : [],
            }));
          }}
        >
          <FormControlLabel
            value="private"
            control={<Radio />}
            label="Private"
          />
          <FormControlLabel value="public" control={<Radio />} label="Public" />
        </RadioGroup>
      </Box>

      {formData.isPublic ? (
        <Typography variant="body2" color="textSecondary">
          This playlist is public and will be accessible to all users.
        </Typography>
      ) : (
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            This playlist is private and only accessible by the users you select
            below.
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 2 }}>
            You are selected by default, but you can untick yourself if you do
            not want access.
          </Typography>

          <FormLabel component="legend" sx={{ mb: 1 }}>
            Select users to share with:
          </FormLabel>

          {usersLoading ? (
            <Typography variant="body2">Loading users...</Typography>
          ) : (
            <>
              {/* 🔍 Search Box */}
              <TextField
                placeholder="Search users by name, email or role"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <List
                sx={{
                  maxHeight: 300,
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                <ListItem button onClick={toggleSelectAll}>
                  <ListItemIcon>
                    <Checkbox checked={allUsersSelected} />
                  </ListItemIcon>
                  <ListItemText primary="Select All" />
                </ListItem>

                {filteredUsers.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No users match your search." />
                  </ListItem>
                )}

                {filteredUsers.map((user) => (
                  <ListItem
                    key={user.id}
                    button
                    onClick={() => toggleUserSelection(user.id)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={formData.selectedUsers.includes(user.id)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        currentUser && user.id === currentUser.id
                          ? `${user.fullName} (${user.role}) - ${user.email} (You)`
                          : `${user.fullName} (${user.role}) - ${user.email}`
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {formErrors.selectedUsers && (
            <Typography color="error" sx={{ mt: 1 }}>
              {formErrors.selectedUsers}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ShareStep;
